import { AfterViewInit, Directive, inject, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { FormControl, FormGroup } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { Viewerlayer } from "../../../model/viewerlayer";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { LayerCommunicatorService } from "../../../services/communicators/layer-communicator.service";
import { OverlayDialogComponent } from "../../../../dialog-components/overlay-dialog/overlay-dialog.component";
import { ILayerResponse } from "../../../interfaces/i-layer-response";
import { ToolbarService } from "../../../services/internal-services/toolbar.service";
import { toolbarOptions } from "../../../../shared/model/toolbar-constants";
import { GeoService } from "../../../services/internal-services/geo.service";
import { FeaturesSearchService } from "../../../services/internal-services/features-search.service";
import { LayerService } from "../../../services/internal-services/layer.service";
import { DocumentService } from "../../../services/internal-services/document.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { MapService } from "../../../services/internal-services/map.service";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../../model/feature-flags";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";

/**
 * Base Layer manager component
 * This should be implemented on each viewer to override constructor
 */
@Directive()
export abstract class BaseLayerManagerComponent implements OnDestroy, AfterViewInit {
  viewerService = inject(VIEWER_SERVICE);

  FeatureFlags = FeatureFlags;

  usedColors: [];

  sourceTypes = ViewerConstants.SOURCE_NAME;

  subscriptions: Subscription[] = [];

  displayPanel = false;

  resizeInfoMessage = false;

  enableDebugOptions = false;

  toolbarOption = null;

  featuresProperties = null;

  layersNames = null;

  filterSelectedLayer = "";

  filterSelectedField = "";

  searchForm = new FormGroup({
    search: new FormControl(""),
    layer: new FormControl(this.filterSelectedLayer),
    field: new FormControl(this.filterSelectedField),
  });

  protected constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected layerCommunicatorService: LayerCommunicatorService,
    protected matDialog: MatDialog,
    protected toolbarService: ToolbarService,
    protected featuresSearchService: FeaturesSearchService,
    protected layerService: LayerService,
    protected flagService: FeatureFlagsService,
  ) {}

  ngAfterViewInit() {
    this.subscriptions.push(
      this.viewerService.developerTools$.subscribe((enable: boolean) => {
        this.enableDebugOptions = enable;
      }),
    );

    this.subscriptions.push(
      this.layerCommunicatorService.shareLayerResponse$.subscribe((response: ILayerResponse) => {
        if (response && response.status && response.status === 200 && response.id) {
          this.viewerService.shareOverlayLayer(response.id);
        }
      }),
    );

    this.subscriptions.push(
      this.layerCommunicatorService.removeLayerResponse$.subscribe((response: ILayerResponse) => {
        if (response && response.status && response.status === 200 && response.id) {
          this.viewerService.removeOverlayLayer(response.id);
        }
      }),
    );

    this.subscriptions.push(
      this.toolbarService.enabledTool$.subscribe((enabledTool) => {
        this.displayPanel = this.toolbarOption === enabledTool;
        this.resizeInfoMessage =
          enabledTool !== "none" &&
          enabledTool !== "measurement_tool" &&
          enabledTool !== "drawing_tool" &&
          enabledTool !== "brightness_contrast_toolbar";
        this.viewerService.resizeInfoMessageSubject.next(this.resizeInfoMessage);
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  fitToBbox(wkt: string, options: any = null) {
    // Check if bbox its polygon or point
    if (wkt.includes("POINT")) {
      this.mapService.setCenterToWktPoint(wkt);
    } else {
      this.mapService.fitToWktPolygon(wkt, ViewerConstants.PROJECTIONS.WebMercator, options);
    }
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_HIGHLIGHT_ON_MAP,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
    // this.viewerService.fitToWktPolygon(wktBbox);
  }

  paintCoverage(wkt: string, name: string) {
    const wktFeature = this.layerService.createFeatureWkt(this.mapService.getCoreMap(), wkt, `EPSG:4326`, { name });
    this.mapService.getDrawingLayer().getSource().addFeature(wktFeature);
    // wkt feature its returned on mercator

    this.mapService.fitToExtent(wktFeature.getGeometry().getExtent(), ViewerConstants.PROJECTIONS.WebMercator);
  }

  shareLayer(layerId: string) {
    const dialog = this.matDialog.open(OverlayDialogComponent, {
      data: {
        text: "Are you sure you want to share this layer with all members of your team?",
        isConfirmation: true,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.layerCommunicatorService.shareLayerRequest({ id: layerId });
      }
    });
  }

  removeLayer(layerId: string) {
    const dialog = this.matDialog.open(OverlayDialogComponent, {
      data: {
        text: "Are you sure you want to remove this layer?",
        isConfirmation: true,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.layerCommunicatorService.removeLayerRequest({ id: layerId });
      }
    });
  }

  removeUserLayer(layerId: string) {
    const dialog = this.matDialog.open(OverlayDialogComponent, {
      data: {
        text: "Are you sure you want to remove this layer?",
        isConfirmation: true,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.viewerService.removeUserLayer(layerId);
      }
    });
  }

  abstract showUploadDialog();

  togglePanelDisplay() {
    this.toolbarService.enableTool(!this.displayPanel ? this.toolbarOption : toolbarOptions.NONE);
  }

  zoomToFeature(layer: Viewerlayer, feature: any) {
    this.viewerService.zoomToFeature(layer, feature);
    if (feature.geometry.type === ViewerConstants.GEOMETRY_TYPE.Point) {
      const geoJSONWGS84 = GeoService.pointToGeoJSONWGS84(feature.geometry.coordinates, true);
      feature.layerName = layer.name;
      feature.headers = layer.customOverlayProperties
        ? layer.customOverlayProperties.layerProperties
        : Object.keys(feature.properties ?? {}).filter((k) => !k.startsWith("_"));
      this.viewerService.clickGetInfoButton(geoJSONWGS84.geometry.coordinates, [feature]);
    }
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_ZOOM_TO_FEATURE,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  toggleLayerContrast(layer: Viewerlayer) {
    this.viewerService.toggleContrastEnhance(layer.id);
  }

  hideOthers(layerId: string) {
    this.viewerService.changeLayerVisibility(layerId, true);
  }

  copyToClipboard(text: string) {
    DocumentService.copyTextToClipboard(text);
  }

  changeLayerVisibility(event: MatCheckboxChange) {
    this.viewerService.changeLayerVisibility(event.source.value, event.checked);

    const layer = this.viewerService.getOverlayLayers().find((overlayLayer) => overlayLayer.id === event.source.value);
    if (!!layer && layer.layerType === "WMSLAYER" && layer.layersAvailable) {
      layer.layersAvailable.map((layerAvailable) => (layerAvailable.visible = event.checked));
      this.changeWMSChildVisibility(null, null, layer);
    }
  }

  changeWMSChildVisibility(event: MatCheckboxChange, childLayer: any, parentLayer: any) {
    // Update parent layer object available layers
    if (childLayer)
      parentLayer.layersAvailable.find((layers) => layers.name === childLayer.name).visible = event.checked;
    // Update parent layer object status
    parentLayer.visible = !!parentLayer.layersAvailable.find((layer) => layer.visible);
    // Update layers on map
    this.mapService.getCoreMap().changeVisibilityWMSChild(parentLayer.id);
  }
}
